"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST = exports.NormalizedSignalDriveModesFormStateCodec = exports.BoundNormalizedSignalDriveModesForm = exports.NormalizedSignalDriveModesFormDataLatestSchema = exports.NormalizedSignalDriveModesFormStateSchema = exports.FormSignalGroupSchema = exports.FormOperatingConditionSchema = exports.OperatingConditionValueSchema = exports.FormThresholdRowSchema = exports.OperatingConditionOperator = exports.ThresholdType = exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION = void 0;
exports.payloadToNormalizedSignalDriveModesFormState = payloadToNormalizedSignalDriveModesFormState;
exports.normalizedSignalDriveModesFormStateToPayload = normalizedSignalDriveModesFormStateToPayload;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const z = require("zod");
const condition_mode_denormalized_1 = require("../../../../../../DataScience/Types/condition-mode-denormalized");
const operating_condition_modes_1 = require("../../operating-condition-modes");
exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION = 'normalized-signal-drive-modes-form/v1';
var ThresholdType;
(function (ThresholdType) {
    ThresholdType["Vih"] = "vih";
    ThresholdType["Vil"] = "vil";
    ThresholdType["Voh"] = "voh";
    ThresholdType["Vol"] = "vol";
})(ThresholdType || (exports.ThresholdType = ThresholdType = {}));
var OperatingConditionOperator;
(function (OperatingConditionOperator) {
    OperatingConditionOperator["Minimum"] = "minimum";
    OperatingConditionOperator["Maximum"] = "maximum";
    OperatingConditionOperator["Range"] = "range";
    OperatingConditionOperator["Absolute"] = "absolute";
})(OperatingConditionOperator || (exports.OperatingConditionOperator = OperatingConditionOperator = {}));
exports.FormThresholdRowSchema = z.object({
    id: z.string(),
    type: z.nativeEnum(ThresholdType),
    min: z.string(),
    typ: z.string(),
    max: z.string(),
    groupIds: z.string().array(),
    conditionIds: z.string().array(),
    driveModes: z.nativeEnum(sigma_engine_shared_1.SignalOutputDriveType).array(),
    variantKey: z.string().optional(),
});
exports.OperatingConditionValueSchema = z
    .object({
    targetSignalId: z.string().nullable(),
    operator: z.nativeEnum(OperatingConditionOperator),
    valueA: z.number(),
    valueB: z.number(),
    relativeSignalId: z.string().nullable(),
})
    .transform((value) => value.operator === OperatingConditionOperator.Range && value.valueA > value.valueB ?
    { ...value, valueA: value.valueB, valueB: value.valueA }
    : value);
exports.FormOperatingConditionSchema = z.object({
    id: z.string(),
    value: exports.OperatingConditionValueSchema,
    readOnly: z.boolean().optional(),
});
exports.FormSignalGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    signalIds: z.string().array(),
    driveModes: z.nativeEnum(sigma_engine_shared_1.SignalOutputDriveType).array(),
});
exports.NormalizedSignalDriveModesFormStateSchema = z.object({
    formVersion: z.literal(exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION),
    signalGroups: exports.FormSignalGroupSchema.array(),
    conditions: exports.FormOperatingConditionSchema.array(),
    thresholds: exports.FormThresholdRowSchema.array(),
});
exports.NormalizedSignalDriveModesFormDataLatestSchema = exports.NormalizedSignalDriveModesFormStateSchema;
class BoundNormalizedSignalDriveModesForm {
    constructor(seed) {
        this.seed = seed;
    }
    toFormData(payload) {
        return payloadToNormalizedSignalDriveModesFormState(payload);
    }
    toCanonical(formState) {
        return normalizedSignalDriveModesFormStateToPayload(formState, this.seed);
    }
}
exports.BoundNormalizedSignalDriveModesForm = BoundNormalizedSignalDriveModesForm;
class NormalizedSignalDriveModesFormStateCodec {
    constructor() {
        this.version = exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION;
        this.formDataSchema = exports.NormalizedSignalDriveModesFormStateSchema;
    }
    bind(seed) {
        return new BoundNormalizedSignalDriveModesForm(seed);
    }
}
exports.NormalizedSignalDriveModesFormStateCodec = NormalizedSignalDriveModesFormStateCodec;
exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST = new NormalizedSignalDriveModesFormStateCodec();
function payloadToNormalizedSignalDriveModesFormState(payload) {
    const inScope = inScopeConditionModes(payload);
    const inScopeConditionIds = new Set(inScope.map(({ mode }) => mode.id));
    const clustersBySignature = new Map();
    for (const signal of payload.signals) {
        if ((signal.supportedDriveModes ?? []).length === 0) {
            continue;
        }
        const signature = driveSignature(signal);
        const cluster = clustersBySignature.get(signature);
        if (cluster === undefined) {
            clustersBySignature.set(signature, { signalIds: [String(signal.id)], representative: signal });
        }
        else {
            cluster.signalIds.push(String(signal.id));
        }
    }
    const orderedClusters = [...clustersBySignature.values()].sort((left, right) => [...left.signalIds]
        .sort()
        .join(',')
        .localeCompare([...right.signalIds].sort().join(',')));
    const nameBySignalId = new Map(payload.signals.map((signal) => [String(signal.id), signal.name]));
    let positionalCounter = 0;
    const groups = orderedClusters.map((cluster, index) => {
        const signalIds = [...cluster.signalIds].sort();
        const driveModes = outputDriveModesOf(cluster.representative);
        const name = signalIds.length === 1 ?
            (nameBySignalId.get(signalIds[0]) ?? signalIds[0])
            : `Signal group ${(positionalCounter += 1)}`;
        return { id: `sdm-group-${index}`, name, signalIds, driveModes };
    });
    const rowByKey = new Map();
    groups.forEach((group, index) => {
        for (const partial of thresholdRowsForSignature(orderedClusters[index].representative, group.driveModes, inScopeConditionIds)) {
            const key = [
                partial.type,
                partial.min,
                partial.max,
                partial.conditionIds.join(','),
                [...partial.driveModes].sort().join(','),
                partial.variantKey ?? '',
            ].join('|');
            const existing = rowByKey.get(key);
            if (existing === undefined) {
                rowByKey.set(key, { id: `sdm-threshold-${rowByKey.size}`, ...partial, groupIds: [group.id] });
            }
            else {
                existing.groupIds.push(group.id);
            }
        }
    });
    return {
        formVersion: exports.NORMALIZED_SIGNAL_DRIVE_MODES_FORM_LATEST_VERSION,
        signalGroups: groups,
        conditions: buildConditionRows(inScope),
        thresholds: [...rowByKey.values()],
    };
}
function normalizedSignalDriveModesFormStateToPayload(formState, seed) {
    assertFormStateReferentialIntegrity(formState, seed);
    const nameBySignalId = new Map(seed.signals.map((signal) => [String(signal.id), signal.name]));
    const seedInScope = inScopeConditionModes(seed);
    const seedInScopeById = new Map(seedInScope.map(({ mode, value }) => [mode.id, { mode, value }]));
    const conditionIdToModeId = new Map();
    const rebuiltInScopeById = new Map();
    for (const condition of formState.conditions) {
        const targetId = condition.value.targetSignalId ?? '';
        const targetName = nameBySignalId.get(targetId) ?? targetId;
        const seeded = seedInScopeById.get(condition.id);
        if (seeded !== undefined && operating_condition_modes_1.OperatingConditionModes.valuesEqual(seeded.value, condition.value)) {
            conditionIdToModeId.set(condition.id, condition.id);
            rebuiltInScopeById.set(condition.id, seeded.mode);
            continue;
        }
        const mode = operating_condition_modes_1.OperatingConditionModes.toMode(condition.value, targetName);
        conditionIdToModeId.set(condition.id, mode.id);
        rebuiltInScopeById.set(mode.id, mode);
    }
    const groupById = new Map(formState.signalGroups.map((group) => [group.id, group]));
    const supportedTypes = new Map();
    const markSupported = (signalId, driveType) => {
        const set = supportedTypes.get(signalId) ?? new Set();
        set.add(driveType);
        supportedTypes.set(signalId, set);
    };
    const groupedSignalIds = new Set();
    for (const group of formState.signalGroups) {
        for (const signalId of group.signalIds) {
            groupedSignalIds.add(signalId);
            for (const driveType of group.driveModes) {
                markSupported(signalId, driveType);
            }
        }
    }
    const onInputThreshold = new Set();
    for (const row of formState.thresholds) {
        if (!isInputThresholdType(row.type)) {
            continue;
        }
        for (const groupId of row.groupIds) {
            for (const signalId of groupById.get(groupId)?.signalIds ?? []) {
                onInputThreshold.add(signalId);
            }
        }
    }
    for (const signal of seed.signals) {
        const signalId = String(signal.id);
        if (!groupedSignalIds.has(signalId)) {
            continue;
        }
        if (isInputLikeSignal(signal) || (signal.signalDirection === undefined && onInputThreshold.has(signalId))) {
            markSupported(signalId, sigma_engine_shared_1.SignalInputDriveType.HighZ);
        }
    }
    const originalParamValues = new Map();
    for (const signal of seed.signals) {
        for (const driveMode of signal.supportedDriveModes ?? []) {
            const parameters = driveMode.parameters ?? {};
            for (const [field, text] of voltageParamEntries(driveMode)) {
                const raw = parameters[field];
                if (raw !== undefined && raw !== null) {
                    originalParamValues.set(`${String(signal.id)}|${String(driveMode.type)}|${field}|${text}`, raw);
                }
            }
        }
    }
    const originalConditionNames = new Map();
    for (const signal of seed.signals) {
        for (const driveMode of signal.supportedDriveModes ?? []) {
            for (const condition of driveMode.conditions ?? []) {
                const mode = conditionModeIdOf(condition);
                if (mode !== undefined && condition.name !== undefined) {
                    originalConditionNames.set(mode, condition.name);
                }
            }
        }
    }
    const seedInScopeIds = new Set(seedInScope.map(({ mode }) => mode.id));
    const unrepresentedConditions = new Map();
    for (const signal of seed.signals) {
        const occurrenceBySignature = new Map();
        for (const driveMode of signal.supportedDriveModes ?? []) {
            const conditionIds = conditionModeIdsOf(driveMode).filter((id) => seedInScopeIds.has(id));
            const signature = `${String(driveMode.type)}|${conditionIds.join(',')}`;
            const occurrence = occurrenceBySignature.get(signature) ?? 0;
            occurrenceBySignature.set(signature, occurrence + 1);
            const unrepresented = (driveMode.conditions ?? []).filter((condition) => conditionModeIdOf(condition) === undefined);
            if (unrepresented.length > 0) {
                const variantKey = occurrence === 0 ? '' : String(occurrence);
                unrepresentedConditions.set(`${String(signal.id)}|${String(driveMode.type)}|${conditionIds.join('|')}#${variantKey}`, unrepresented);
            }
        }
    }
    const accumulator = new Map();
    const accumulatorFor = (signalId, driveType, conditionSetKey, conditionModeIds) => {
        const byType = accumulator.get(signalId) ?? new Map();
        const byConditionSet = byType.get(driveType) ?? new Map();
        const entry = byConditionSet.get(conditionSetKey) ?? { params: {}, conditionModeIds, conditionSetKey };
        byConditionSet.set(conditionSetKey, entry);
        byType.set(driveType, byConditionSet);
        accumulator.set(signalId, byType);
        return entry;
    };
    for (const row of formState.thresholds) {
        const { field, slot } = THRESHOLD_PARAM[row.type];
        const value = slot === 'min' ? row.min : row.max;
        const conditionModeIds = row.conditionIds.map((id) => conditionIdToModeId.get(id) ?? id).sort();
        const conditionSetKey = `${conditionModeIds.join('|')}#${row.variantKey ?? ''}`;
        for (const groupId of row.groupIds) {
            const group = groupById.get(groupId);
            if (group === undefined) {
                continue;
            }
            const driveTypes = isInputThresholdType(row.type) ? [sigma_engine_shared_1.SignalInputDriveType.HighZ]
                : row.driveModes.length > 0 ? group.driveModes.filter((mode) => row.driveModes.includes(mode))
                    : group.driveModes;
            for (const signalId of group.signalIds) {
                for (const driveType of driveTypes) {
                    const entry = accumulatorFor(signalId, driveType, conditionSetKey, conditionModeIds);
                    if (value !== '') {
                        entry.params[field] = originalParamValues.get(`${signalId}|${driveType}|${field}|${value}`) ?? value;
                    }
                }
            }
        }
    }
    const signals = seed.signals.map((signal) => {
        const signalId = String(signal.id);
        const driveTypes = supportedTypes.get(signalId);
        if (driveTypes === undefined || driveTypes.size === 0) {
            return { ...signal, supportedDriveModes: [] };
        }
        const supportedDriveModes = [];
        for (const driveType of [...driveTypes].sort()) {
            const entries = accumulator.get(signalId)?.get(driveType);
            if (entries === undefined || entries.size === 0) {
                supportedDriveModes.push({ type: driveType });
                continue;
            }
            for (const entry of entries.values()) {
                const driveMode = { type: driveType };
                if (Object.keys(entry.params).length > 0) {
                    driveMode.parameters = entry.params;
                }
                const conditions = entry.conditionModeIds.map((mode) => {
                    const name = originalConditionNames.get(mode);
                    return name === undefined ?
                        { condition: { mode, mode_state: true } }
                        : { name, condition: { mode, mode_state: true } };
                });
                conditions.push(...(unrepresentedConditions.get(`${signalId}|${driveType}|${entry.conditionSetKey}`) ?? []));
                if (conditions.length > 0) {
                    driveMode.conditions = conditions;
                }
                supportedDriveModes.push(driveMode);
            }
        }
        return { ...signal, supportedDriveModes };
    });
    const supersededModeIds = new Set();
    for (const [conditionId, modeId] of conditionIdToModeId) {
        if (conditionId !== modeId && seedInScopeById.has(conditionId)) {
            supersededModeIds.add(conditionId);
        }
    }
    const modes = [];
    const seenModeIds = new Set();
    for (const mode of seed.modes) {
        if (supersededModeIds.has(mode.id)) {
            continue;
        }
        modes.push(mode);
        seenModeIds.add(mode.id);
    }
    for (const [modeId, mode] of rebuiltInScopeById) {
        if (!seenModeIds.has(modeId)) {
            modes.push(mode);
            seenModeIds.add(modeId);
        }
    }
    return { signals, modes };
}
function assertFormStateReferentialIntegrity(formState, seed) {
    const groupIds = new Set(formState.signalGroups.map((group) => group.id));
    const conditionIds = new Set(formState.conditions.map((condition) => condition.id));
    const seedSignalIds = new Set(seed.signals.map((signal) => String(signal.id)));
    const violations = [];
    for (const group of formState.signalGroups) {
        for (const signalId of group.signalIds) {
            if (!seedSignalIds.has(signalId)) {
                violations.push(new Error(`group "${group.id}" references unknown signal "${signalId}"`));
            }
        }
    }
    for (const row of formState.thresholds) {
        for (const groupId of row.groupIds) {
            if (!groupIds.has(groupId)) {
                violations.push(new Error(`threshold "${row.id}" references unknown group "${groupId}"`));
            }
        }
        for (const conditionId of row.conditionIds) {
            if (!conditionIds.has(conditionId)) {
                violations.push(new Error(`threshold "${row.id}" references unknown condition "${conditionId}"`));
            }
        }
    }
    if (violations.length > 0) {
        const summary = `NormalizedSignalDriveModesForm state has ${violations.length} dangling reference(s)`;
        throw new AggregateError(violations, summary);
    }
}
function inScopeConditionModes(payload) {
    const referenced = new Set();
    for (const signal of payload.signals) {
        for (const driveMode of signal.supportedDriveModes ?? []) {
            for (const modeId of conditionModeIdsOf(driveMode)) {
                referenced.add(modeId);
            }
        }
    }
    const modeById = new Map(payload.modes.map((mode) => [mode.id, mode]));
    const inScope = [];
    for (const modeId of [...referenced].sort()) {
        const mode = modeById.get(modeId);
        if (mode === undefined) {
            continue;
        }
        const [denormalized] = (0, condition_mode_denormalized_1.convertModeBaseToConditionModeDenormalized)(mode, payload.signals);
        if (denormalized === undefined) {
            continue;
        }
        const value = operating_condition_modes_1.OperatingConditionModes.toValue(denormalized);
        if (value === null) {
            continue;
        }
        inScope.push({ mode, value });
    }
    return inScope;
}
function buildConditionRows(inScope) {
    return inScope.map(({ mode, value }) => ({ id: mode.id, value, readOnly: true }));
}
function thresholdRowsForSignature(signal, groupOutputModes, inScopeConditionIds) {
    const clusters = new Map();
    const occurrenceBySignature = new Map();
    for (const driveMode of signal.supportedDriveModes ?? []) {
        const conditionIds = conditionModeIdsOf(driveMode).filter((id) => inScopeConditionIds.has(id));
        const signature = `${String(driveMode.type)}|${conditionIds.join(',')}`;
        const occurrence = occurrenceBySignature.get(signature) ?? 0;
        occurrenceBySignature.set(signature, occurrence + 1);
        const variantKey = occurrence === 0 ? undefined : String(occurrence);
        for (const [field, value] of voltageParamEntries(driveMode)) {
            const type = FIELD_THRESHOLD[field];
            const key = `${type}|${value}|${conditionIds.join(',')}|${variantKey ?? ''}`;
            const cluster = clusters.get(key) ?? {
                type,
                value,
                conditionIds,
                driveTypes: new Set(),
                ...(variantKey === undefined ? {} : { variantKey }),
            };
            cluster.driveTypes.add(String(driveMode.type));
            clusters.set(key, cluster);
        }
    }
    const groupOutputsSorted = [...groupOutputModes].sort();
    return [...clusters.values()].map((cluster) => {
        const { slot } = THRESHOLD_PARAM[cluster.type];
        const clusterOutputs = [...cluster.driveTypes].filter((type) => OUTPUT_DRIVE_TYPES.has(type)).sort();
        const coversAllOutputs = clusterOutputs.length === groupOutputsSorted.length &&
            clusterOutputs.every((mode, index) => mode === groupOutputsSorted[index]);
        const driveModes = isInputThresholdType(cluster.type) || coversAllOutputs ? [] : clusterOutputs;
        return {
            type: cluster.type,
            min: slot === 'min' ? cluster.value : '',
            typ: '',
            max: slot === 'max' ? cluster.value : '',
            conditionIds: [...cluster.conditionIds].sort(),
            driveModes,
            ...(cluster.variantKey === undefined ? {} : { variantKey: cluster.variantKey }),
        };
    });
}
function driveSignature(signal) {
    const driveModes = (signal.supportedDriveModes ?? [])
        .map((driveMode) => ({
        type: String(driveMode.type),
        params: voltageParamEntries(driveMode)
            .map(([field, value]) => `${field}=${value}`)
            .sort(),
        conditions: conditionModeIdsOf(driveMode),
    }))
        .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
    return JSON.stringify(driveModes);
}
function outputDriveModesOf(signal) {
    const modes = new Set();
    for (const driveMode of signal.supportedDriveModes ?? []) {
        if (OUTPUT_DRIVE_TYPES.has(String(driveMode.type))) {
            modes.add(driveMode.type);
        }
    }
    return [...modes].sort();
}
function voltageParamEntries(driveMode) {
    const parameters = driveMode.parameters ?? {};
    const entries = [];
    for (const field of VOLTAGE_FIELDS) {
        const raw = parameters[field];
        if (raw !== undefined && raw !== null) {
            entries.push([field, String(raw)]);
        }
    }
    return entries;
}
function conditionModeIdOf(condition) {
    const inner = condition.condition;
    if (typeof inner !== 'object' || inner === null || !('mode' in inner)) {
        return undefined;
    }
    const { mode } = inner;
    return typeof mode === 'string' ? mode : undefined;
}
function conditionModeIdsOf(driveMode) {
    return (driveMode.conditions ?? [])
        .map((condition) => conditionModeIdOf(condition))
        .filter((mode) => typeof mode === 'string')
        .sort();
}
function isInputThresholdType(type) {
    return type === ThresholdType.Vih || type === ThresholdType.Vil;
}
function isInputLikeSignal(signal) {
    return signal.signalDirection === sigma_engine_shared_1.SignalDirection.Input || signal.signalDirection === sigma_engine_shared_1.SignalDirection.BiDirectionalIO;
}
const VOLTAGE_FIELDS = [
    'outputHighVoltage',
    'outputLowVoltage',
    'inputHighVoltage',
    'inputLowVoltage',
];
const THRESHOLD_PARAM = {
    [ThresholdType.Voh]: { field: 'outputHighVoltage', slot: 'min' },
    [ThresholdType.Vol]: { field: 'outputLowVoltage', slot: 'max' },
    [ThresholdType.Vih]: { field: 'inputHighVoltage', slot: 'min' },
    [ThresholdType.Vil]: { field: 'inputLowVoltage', slot: 'max' },
};
const FIELD_THRESHOLD = {
    outputHighVoltage: ThresholdType.Voh,
    outputLowVoltage: ThresholdType.Vol,
    inputHighVoltage: ThresholdType.Vih,
    inputLowVoltage: ThresholdType.Vil,
};
const OUTPUT_DRIVE_TYPES = new Set(Object.values(sigma_engine_shared_1.SignalOutputDriveType));
//# sourceMappingURL=form-latest.js.map